/**
 * HTTP-клиент для взаимодействия с REST API.
 *
 * Использует библиотеку axios с автоматической подстановкой
 * JWT-токена из localStorage в заголовок Authorization.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

/** Базовый URL API сервера */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Создаёт настроенный экземпляр axios.
 * Перехватчик запросов автоматически добавляет JWT-токен.
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Перехватчик: добавляет токен авторизации к каждому запросу
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Механизм автоматического обновления access-токена при 401
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Если 401 и это не повторный запрос и не запрос на refresh/login
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      if (isRefreshing) {
        // Ставим запрос в очередь, пока идёт refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject: (err: unknown) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        isRefreshing = false;
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const newToken = data.token;
        localStorage.setItem('auth_token', newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

/* ============================================================
 * Функции API, сгруппированные по модулям
 * ============================================================ */

/** Модуль аутентификации */
export const authAPI = {
  register: (data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: string;
    birth_date?: string;
    diagnosis_date?: string;
  }) => apiClient.post('/auth/register', data),

  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),

  refresh: (refreshToken: string) =>
    apiClient.post('/auth/refresh', { refresh_token: refreshToken }),

  logout: (refreshToken: string) =>
    apiClient.post('/auth/logout', { refresh_token: refreshToken }),

  getMe: () => apiClient.get('/auth/me'),

  requestReset: (email: string) =>
    apiClient.post('/auth/request-reset', { email }),

  resetPassword: (token: string, newPassword: string) =>
    apiClient.post('/auth/reset-password', { token, new_password: newPassword }),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiClient.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

/** Модуль пациента */
export const patientAPI = {
  getProfile: () => apiClient.get('/patient/profile'),

  updateProfile: (data: {
    birth_date?: string;
    diagnosis_date?: string;
    ms_type?: string;
  }) => apiClient.put('/patient/profile', data),

  getSymptoms: () => apiClient.get('/patient/symptoms'),

  createDailyReport: (data: {
    date: string;
    overall_feeling: number;
    notes?: string;
    symptoms: { symptom_id: number; severity: number }[];
    medications: { name: string; taken: boolean }[];
  }) => apiClient.post('/patient/daily-reports', data),

  getDailyReports: (params?: { from?: string; to?: string; limit?: number; offset?: number }) =>
    apiClient.get('/patient/daily-reports', { params }),

  getDailyReport: (id: number) => apiClient.get(`/patient/daily-reports/${id}`),

  updateDailyReport: (id: number, data: {
    overall_feeling?: number;
    notes?: string;
    symptoms?: { symptom_id: number; severity: number }[];
    medications?: { name: string; taken: boolean }[];
  }) => apiClient.put(`/patient/daily-reports/${id}`, data),

  deleteDailyReport: (id: number) => apiClient.delete(`/patient/daily-reports/${id}`),
};

/** Модуль тестирования */
export const testsAPI = {
  getTests: () => apiClient.get('/tests/'),

  submitResult: (data: {
    test_id: number;
    score: number;
    details?: Record<string, unknown>;
  }) => apiClient.post('/tests/results', data),

  getResults: (params?: { test_type?: string; limit?: number; offset?: number }) =>
    apiClient.get('/tests/results', { params }),
};

/** Модуль аналитики */
export const analyticsAPI = {
  getStatus: () => apiClient.get('/analytics/status'),

  getHistory: (weeks?: number) =>
    apiClient.get('/analytics/history', { params: { weeks } }),

  getForecast: (weeksAhead?: number) =>
    apiClient.get('/analytics/forecast', { params: { weeks_ahead: weeksAhead } }),

  getRecommendations: () => apiClient.get('/analytics/recommendations'),

  getCorrelations: (days?: number) =>
    apiClient.get('/analytics/correlations', { params: { days } }),

  getHeatmap: (year?: number, month?: number) =>
    apiClient.get('/analytics/heatmap', { params: { year, month } }),

  getDashboardExtended: (days?: number) =>
    apiClient.get('/analytics/dashboard-extended', { params: { days } }),
};

/** Модуль обострений (пациент) */
export const relapseAPI = {
  create: (data: {
    start_date: string;
    end_date?: string;
    severity: string;
    affected_systems?: string;
    treatment?: string;
    notes?: string;
  }) => apiClient.post('/patient/relapses', data),

  getList: (params?: { limit?: number; offset?: number }) =>
    apiClient.get('/patient/relapses', { params }),

  update: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/patient/relapses/${id}`, data),

  delete: (id: number) => apiClient.delete(`/patient/relapses/${id}`),
};

/** Модуль врача */
export const doctorAPI = {
  getDashboard: () => apiClient.get('/doctor/dashboard'),

  getAuditLog: (params?: { limit?: number; offset?: number }) =>
    apiClient.get('/doctor/audit-log', { params }),

  getPatients: () => apiClient.get('/doctor/patients'),

  getPatientDetail: (patientId: number) =>
    apiClient.get(`/doctor/patients/${patientId}`),

  getPatientHistory: (patientId: number, params?: {
    from?: string;
    to?: string;
    limit?: number;
  }) => apiClient.get(`/doctor/patients/${patientId}/history`, { params }),

  getChartData: (patientId: number, days?: number) =>
    apiClient.get(`/doctor/patients/${patientId}/chart-data`, {
      params: { days },
    }),

  assignPatient: (patientId: number) =>
    apiClient.post(`/doctor/patients/${patientId}/assign`),

  getUnassignedPatients: (search?: string) =>
    apiClient.get('/doctor/patients/unassigned', { params: { search } }),

  getNotes: (patientId: number) =>
    apiClient.get(`/doctor/patients/${patientId}/notes`),

  createNote: (patientId: number, text: string) =>
    apiClient.post(`/doctor/patients/${patientId}/notes`, { text }),

  deleteNote: (patientId: number, noteId: number) =>
    apiClient.delete(`/doctor/patients/${patientId}/notes/${noteId}`),

  getAlertSettings: (patientId: number) =>
    apiClient.get(`/doctor/patients/${patientId}/alert-settings`),

  updateAlertSettings: (patientId: number, data: Record<string, number>) =>
    apiClient.put(`/doctor/patients/${patientId}/alert-settings`, data),

  comparePeriods: (patientId: number, params: {
    from1: string; to1: string; from2: string; to2: string;
  }) => apiClient.get(`/doctor/patients/${patientId}/compare`, { params }),

  exportPatientData: (patientId: number, type: 'reports' | 'tests') =>
    apiClient.get(`/doctor/patients/${patientId}/export`, {
      params: { type },
      responseType: 'blob',
    }),

  downloadPdfReport: (patientId: number) =>
    apiClient.get(`/doctor/patients/${patientId}/report-pdf`, {
      responseType: 'blob',
    }),

  getPatientRelapses: (patientId: number) =>
    apiClient.get(`/doctor/patients/${patientId}/relapses`),

  downloadResearchExport: (anonymize: boolean = true) =>
    apiClient.get('/doctor/research-export', {
      params: { anonymize },
      responseType: 'blob',
    }),
};

/** Модуль EDSS */
export const edssAPI = {
  createAssessment: (data: {
    patient_id: number;
    score: number;
    functional_systems?: Record<string, number>;
    notes?: string;
  }) => apiClient.post('/edss/assess', data),

  getHistory: (patientId: number) =>
    apiClient.get(`/edss/history/${patientId}`),

  getLatest: (patientId: number) =>
    apiClient.get(`/edss/latest/${patientId}`),
};

/** Модуль уведомлений */
export const notificationsAPI = {
  getNotifications: (unreadOnly?: boolean, params?: { limit?: number; offset?: number }) =>
    apiClient.get('/notifications/', {
      params: { unread_only: unreadOnly, ...params },
    }),

  markRead: (id: number) => apiClient.put(`/notifications/${id}/read`),

  markAllRead: () => apiClient.put('/notifications/read-all'),
};

/** Модуль сообщений */
export const messagesAPI = {
  getConversations: () => apiClient.get('/messages/conversations'),

  getMessages: (userId: number, params?: { limit?: number; offset?: number }) =>
    apiClient.get(`/messages/${userId}`, { params }),

  sendMessage: (userId: number, text: string) =>
    apiClient.post(`/messages/${userId}`, { text }),
};
